import { toast } from 'react-toastify';

export interface ApiError {
  message: string;
  status?: number;
  details?: string;
}

export interface ApiErrorResponse {
  error: string;
  errorClass: string;
  statusCode: number;
}

export class ErrorHandler {
  static handleError(error: any, context?: string): void {
    console.error('Error occurred:', error, context ? `Context: ${context}` : '');
    
    let errorMessage = 'An unexpected error occurred';
    let errorDetails = '';
    
    // Handle different types of errors
    if (error?.response && typeof error.response === 'string' && error?.status) {
      // This is likely an ApiException from the generated client
      const status = error.status;
      
      try {
        // Try to parse the response as JSON (our structured server error format)
        const responseData = JSON.parse(error.response);
        
        // Check if it's our standard server error format
        if (responseData && typeof responseData === 'object' && 'error' in responseData && 'errorClass' in responseData) {
          const serverError = responseData as ApiErrorResponse;
          errorMessage = serverError.error;
        } else {
          // Fallback for non-standard JSON responses
          errorMessage = `Server Error (${status})`;
          
          if (responseData?.message) {
            errorDetails = responseData.message;
          } else if (typeof responseData === 'string') {
            errorDetails = responseData;
          }
        }
      } catch (parseError) {
        // Response is not valid JSON, treat as plain text
        errorMessage = `Server Error (${status})`;
        errorDetails = error.response;
      }
      
      // Specific status code handling for authentication/authorization
      if (status === 401 && !errorDetails) {
        return; // Don't show toast for 401 errors, handled by auth logic
      } else if (status === 403 && !errorDetails) {
        errorMessage = 'Access denied';
        errorDetails = 'You do not have permission to perform this action';
      }
    } else if (error?.response) {
      // Legacy error format (axios-style)
      const status = error.response.status;
      const responseData = error.response.data;
      
      // Check if it's our standard server error format
      if (responseData && typeof responseData === 'object' && 'error' in responseData && 'errorClass' in responseData) {
        const serverError = responseData as ApiErrorResponse;
        errorMessage = serverError.error;
      } else {
        // Fallback for non-standard responses
        errorMessage = `Server Error (${status})`;
        
        if (responseData?.message) {
          errorDetails = responseData.message;
        } else if (typeof responseData === 'string') {
          errorDetails = responseData;
        }
        
        // Specific status code handling for non-standard responses
        switch (status) {
          case 401:
            errorMessage = 'Authentication required';
            errorDetails = 'Please log in to continue';
            break;
          case 403:
            errorMessage = 'Access denied';
            errorDetails = 'You do not have permission to perform this action';
            break;
          case 404:
            errorMessage = 'Not found';
            errorDetails = 'The requested resource was not found';
            break;
          case 500:
            errorMessage = 'Server error';
            errorDetails = 'An internal server error occurred';
            break;
        }
      }
    } else if (error?.message) {
      // JavaScript error or custom error
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }
    
    // Add context if provided
    if (context) {
      errorMessage = `${context}: ${errorMessage}`;
    }
    
    // Show toast notification
    toast.error(
      <div>
        <strong>{errorMessage}</strong>
        {errorDetails && <div className="mt-1 text-sm opacity-80">{errorDetails}</div>}
      </div>,
      {
        toastId: `error-${Date.now()}`, // Prevent duplicate toasts
      }
    );
  }
  
  static handleApiError(error: any, operation?: string): void {
    const context = operation ? `${operation} failed` : 'API call failed';
    this.handleError(error, context);
  }
  
  static showSuccess(message: string): void {
    toast.success(message);
  }
  
  static showInfo(message: string): void {
    toast.info(message);
  }
  
  static showWarning(message: string): void {
    toast.warning(message);
  }
}

// Global error handler for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  ErrorHandler.handleError(event.reason);
  
  // Prevent the default browser behavior (console error)
  event.preventDefault();
});

// Global error handler for uncaught JavaScript errors
window.addEventListener('error', (event) => {
  console.error('Uncaught error:', event.error);
  ErrorHandler.handleError(event.error);
});